import { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaCheck, FaPlus } from 'react-icons/fa';

interface ExternalUser {
  first_name: string;
  last_name: string;
  email?: string;
}

interface ExternalUserForm {
  fullName: string;
  email: string;
}

interface AttendeeSelectionModalWithExternalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  users: any[];
  selectedUsers: (string | ExternalUser)[];
  maxUsers: number;
  onConfirm: (selectedUsers: (string | ExternalUser)[]) => void;
  allowExternal?: boolean;
}

export const AttendeeSelectionModalWithExternal = ({
  isOpen,
  onClose,
  title,
  users,
  selectedUsers,
  maxUsers,
  onConfirm,
  allowExternal = false
}: AttendeeSelectionModalWithExternalProps) => {
  const [localSelection, setLocalSelection] = useState<(string | ExternalUser)[]>(selectedUsers);
  const [showAddExternal, setShowAddExternal] = useState(false);
  const [externalUserForm, setExternalUserForm] = useState<ExternalUserForm>({
    fullName: '',
    email: ''
  });

  useEffect(() => {
    setLocalSelection(selectedUsers);
  }, [selectedUsers, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowAddExternal(false);
      setExternalUserForm({ fullName: '', email: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUserToggle = (userId: string) => {
    setLocalSelection(prev => {
      const isSelected = prev.some(item => typeof item === 'string' && item === userId);
      if (isSelected) {
        return prev.filter(item => !(typeof item === 'string' && item === userId));
      } else if (prev.length < maxUsers) {
        return [...prev, userId];
      }
      return prev;
    });
  };

  const handleRemoveExternal = (index: number) => {
    setLocalSelection(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExternal = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!externalUserForm.fullName.trim()) {
      return;
    }

    if (localSelection.length >= maxUsers) {
      return;
    }

    // Parse full name into first and last name
    const nameParts = externalUserForm.fullName.trim().split(/\s+/);
    const first_name = nameParts[0] || '';
    const last_name = nameParts.slice(1).join(' ') || ''; // If only one word, last_name is empty

    const newUser: ExternalUser = {
      first_name,
      last_name: last_name || first_name, // Use first_name as fallback for last_name if empty
      ...(externalUserForm.email.trim() && { email: externalUserForm.email.trim() })
    };

    setLocalSelection(prev => [...prev, newUser]);
    setExternalUserForm({ fullName: '', email: '' });
    setShowAddExternal(false);
  };

  const handleConfirm = () => {
    onConfirm(localSelection);
    onClose();
  };

  const getUserName = (user: any) => {
    return `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
  };

  const getExternalUserName = (user: ExternalUser) => {
    // If first_name and last_name are the same (single word input), show only once
    if (user.first_name === user.last_name) {
      return user.first_name;
    }
    return `${user.first_name} ${user.last_name}`;
  };

  const isUserSelected = (userId: string) => {
    return localSelection.some(item => typeof item === 'string' && item === userId);
  };

  const externalUsers = localSelection.filter(item => typeof item === 'object') as ExternalUser[];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <h3 className="text-lg font-semibold text-[#171717]">{title}</h3>
          <button
            onClick={onClose}
            className="text-[#aaa] hover:text-[#555] transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-[#555]">
              Select up to {maxUsers} attendees ({localSelection.length}/{maxUsers} selected)
            </p>
          </div>

          {/* External Users Section - Modern Design */}
          {allowExternal && externalUsers.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-[#888] uppercase tracking-wide mb-2.5">External Contacts</h4>
              <div className="space-y-2">
                {externalUsers.map((user, index) => {
                  const actualIndex = localSelection.findIndex(item => 
                    typeof item === 'object' && 
                    item.email === user.email &&
                    item.first_name === user.first_name &&
                    item.last_name === user.last_name
                  );
                  
                  return (
                    <div
                      key={`external-${index}`}
                      className="group flex items-center p-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 hover:shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 shadow-[0_1px_2px_rgba(0,0,0,0.04)] flex-shrink-0">
                          <FaUser className="w-4 h-4 text-white" />
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#171717] truncate">
                            {getExternalUserName(user)}
                          </div>
                          {user.email && (
                            <div className="text-xs text-[#555] truncate">{user.email}</div>
                          )}
                        </div>

                        {/* External Badge */}
                        <div className="flex-shrink-0 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                          External
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveExternal(actualIndex)}
                          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[#aaa] hover:text-[#dc2626] hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                        >
                          <FaTimes className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add External User Form - Modern Design */}
          {allowExternal && showAddExternal && (
            <div className="mb-4 p-5 border border-[#eaeaea] rounded-xl bg-gradient-to-br from-[#fafafa] to-[#f5f5f5] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-[#171717]">Add External Contact</h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddExternal(false);
                    setExternalUserForm({ fullName: '', email: '' });
                  }}
                  className="text-[#aaa] hover:text-[#555] transition-colors"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
              <form onSubmit={handleAddExternal} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[#444] mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={externalUserForm.fullName}
                    onChange={(e) => setExternalUserForm(prev => ({ ...prev, fullName: e.target.value }))}
                    placeholder="e.g. Олег Тихонов"
                    className="w-full px-3.5 py-2.5 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888] transition-all placeholder-[#aaa]"
                    required
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-[#888]">Enter first and last name separated by space</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#444] mb-1.5">
                    Email <span className="text-[#aaa] text-xs font-normal">(optional)</span>
                  </label>
                  <input
                    type="email"
                    value={externalUserForm.email}
                    onChange={(e) => setExternalUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                    className="w-full px-3.5 py-2.5 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888] transition-all placeholder-[#aaa]"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#171717] rounded-lg hover:bg-[#333] active:bg-blue-800 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                  >
                    Add Contact
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddExternal(false);
                      setExternalUserForm({ fullName: '', email: '' });
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-[#444] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Add External Button - Modern Design */}
          {allowExternal && !showAddExternal && localSelection.length < maxUsers && (
            <button
              onClick={() => setShowAddExternal(true)}
              className="w-full mb-4 flex items-center justify-center gap-2 p-3.5 border-2 border-dashed border-[#eaeaea] rounded-xl text-[#171717] hover:border-[#888] hover:bg-[#f5f5f5] hover:text-[#171717] transition-all group"
            >
              <div className="w-6 h-6 rounded-full bg-[#f5f5f5] flex items-center justify-center group-hover:bg-[#eaeaea] transition-colors">
                <FaPlus className="w-3 h-3" />
              </div>
              <span className="text-sm font-medium">Add External Contact</span>
            </button>
          )}

          {/* Project Users Section */}
          <div>
            {users.length > 0 && (
              <h4 className="text-xs font-semibold text-[#888] uppercase tracking-wide mb-2.5">Project Team</h4>
            )}
            <div className="space-y-2">
              {users.map((user) => {
                const userId = user.user_id || user.user?.id || user.id;
                const isSelected = isUserSelected(userId);
                const isDisabled = !isSelected && localSelection.length >= maxUsers;
                
                return (
                  <div
                    key={userId}
                    onClick={() => !isDisabled && handleUserToggle(userId)}
                    className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-[#f5f5f5] border-[#eaeaea] ring-1 ring-blue-500'
                        : isDisabled
                        ? 'bg-[#fafafa] border-[#eaeaea] cursor-not-allowed opacity-60'
                        : 'bg-white border-[#eaeaea] hover:bg-[#fafafa]'
                    }`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isSelected ? 'bg-[#555]' : 'bg-[#eaeaea]'
                      }`}>
                        {isSelected ? (
                          <FaCheck className="w-3 h-3 text-white" />
                        ) : (
                          <FaUser className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-[#888]'}`} />
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1">
                        <div className={`font-medium ${isSelected ? 'text-[#171717]' : 'text-[#171717]'}`}>
                          {getUserName(user)}
                        </div>
                        {user.user?.email && (
                          <div className="text-xs text-[#888]">{user.user.email}</div>
                        )}
                      </div>

                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? 'bg-[#555] border-[#888]'
                          : 'border-[#eaeaea]'
                      }`}>
                        {isSelected && <FaCheck className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {users.length === 0 && !allowExternal && (
              <div className="text-center py-8 text-[#888]">
                <FaUser className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
                <p>No users available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[#eaeaea]">
          <div className="text-sm text-[#888]">
            {localSelection.length} of {maxUsers} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#555] bg-[#f5f5f5] rounded-lg hover:bg-[#eaeaea] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-[#171717] rounded-lg hover:bg-[#333] transition-colors"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

